//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IListeningTopicFilters } from '@apis/listening/listeningApiModel';
import { EcommerceSessionType } from '@apis/metrics/metricsApiModel';
import { CampaignSource } from '@models/CampaignSource';
import CollectionId from '@models/CollectionId';
import FileId from '@models/FileId';
import { IContentItemMetadata } from '@models/IItemMetadata';
import { IProductBasic } from '@models/IProduct';
import { InstagramPostType } from '@models/InstagramPostType';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { UniversalItemType } from '@models/UniversalItemType';
import VideoId from '@models/VideoId';

export enum RIGHTS_STATUS {
  APPROVED = 'Approved',
  EXPIRED = 'Expired',
  FAILED_TO_SEND = 'FailedToSend',
  NOT_SET = 'NotSet',
  OWNED = 'Owned',
  PENDING = 'Pending',
  QUEUED = 'Queued',
}

export interface SemanticTag {
  isExclusion?: boolean;
  minConfidence?: number;
  tag: string;
}

export enum NetworkType {
  BAZAARVOICE = 'Bazaarvoice',
  FACEBOOK = 'Facebook',
  INSTAGRAM = 'Instagram',
  MEDIA_IMPORT = 'MediaImport',
  PINTEREST = 'Pinterest',
  SNAPCHAT = 'Snapchat',
  TIKTOK = 'TikTok',
  TUMBLR = 'Tumblr',
  TWITTER = 'Twitter',
  YOUTUBE = 'YouTube',
}

export interface IContentLibraryFiltersOptions {
  createdAfter?: number;
  dateRange?: string;
  distribution?: string[];
  filter?: string;
  hasLabels?: boolean;
  hasProducts?: boolean;
  igHashtags?: string[];
  igUsernames?: string[];
  includeMissingMedia?: boolean;
  isDistributed?: boolean;
  isScheduled?: boolean;
  itemId?: string;
  itemTypes?: UniversalItemType[];
  labels?: string[];
  mediaItemTypes?: string[];
  networks?: NetworkType[];
  orientation?: string[];
  rightsStatuses?: RIGHTS_STATUS[];
  searchText?: string;
  semanticTags?: SemanticTag[];
  spamFiltering?: boolean;
  userIds?: string[];
}

export type MediaFilterParamKey = keyof IContentLibraryFiltersOptions;

export interface IContentLibrarySourcesOptions {
  accountMentionIds?: string[];
  brandFeedPost?: boolean;
  brandStoryPost?: boolean;
  brandTikTokPosts?: string[];
  campaignsRequests?: CampaignContentRequestSource[];
  collectionId?: string;
  contentGroup?: ContentLibraryContentGroup;
  curalateUserIds?: string[];
  galleryUploaders?: string[];
  instagramUsernames?: string[];
  instagramMentionedUsernames?: string[];
  pinterestUserIds?: string[];
  shareCreatorClients?: string[];
  sourcesGroups?: ContentLibrarySourcesGroups[];
  trackedBusinessUserIds?: string[];
  trackedHashtags?: string[];
  trackedUserIds?: string[];
  tiktokMentionedUserIds?: string[];
}

export type MediaSourceParamKey = keyof IContentLibrarySourcesOptions;

export interface IContentLibraryItemParams {
  filters?: IContentLibraryFiltersOptions;
  hydrateWithDeetsItems?: boolean;
  shouldHydrateAdDetails?: boolean;
  shouldGetOnsiteRevenue?: boolean;
  ecommerceSessionType?: EcommerceSessionType;
  sources?: IContentLibrarySourcesOptions;
  itemIds?: PartialCuid[];
  limit?: number;
  offset?: number;
  orderBy?: ContentLibrarySortType;
  isOrderAscending?: boolean;
  socialFiltersV2Request?: IListeningTopicFilters;
}

export interface IContentItemDimensions {
  height?: number;
  width?: number;
}

export interface IContentForecastData {
  score: number;
  expectedHighPerformance: boolean;
}

export interface IContentLibraryItem {
  addedToCollectionTimestamp?: number;
  addedToLibraryTimestamp?: number;
  adMetadata?: IMediaAdMetadata;
  cameFromSyndication: boolean;
  createdTime: number;
  description?: string;
  dimensions: IContentItemDimensions;
  fileId?: FileId;
  forecastedPerformance?: IContentForecastData;
  id: ItemId;
  imageUrl: string;
  ingestionSource: MediaImportItemIngestionSources;
  isVideoMuted?: boolean;
  itemType: UniversalItemType;
  labels?: string; // Our APIs return all labels in a single comma-separated string
  metadata: IContentItemMetadata;
  misDetails?: IContentItemMISDetails;
  onSiteRevenue?: number;
  onSiteRevenueCurrency?: string;
  originalImageUrl: string;
  products?: IProductBasic[];
  sentimentMetadata?: ISentiment;
  socialDetails?: IContentItemSocialDetails;
  source?: string;
  thumbnailUrl: string;
  videoId?: VideoId;
  videoStatus?: 'Failed' | 'Processing' | 'Ready' | 'Unknown';
}

export interface ISentiment {
  sentiment: 'Positive' | 'Negative' | 'Neutral';
}

export interface IMediaAdMetadata {
  adCode?: IAdCodeDetails;
}

export interface IAdCodeDetails {
  adCode: string;
  createdAt: Date;
  expirationTime?: Date;
  id: string;
}

export interface IDeserializedContentLibraryItem
  extends Omit<
    IContentLibraryItem,
    'addedToCollectionTimestamp' | 'addedToLibraryTimestamp' | 'createdTime'
  > {
  addedToCollectionTimestamp?: Date;
  addedToLibraryTimestamp?: Date;
  createdTime: Date;
}

export interface IContentLibraryItemStats {
  comments?: number;
  completionRate?: number;
  computedImpressions?: number;
  computedReach?: number;
  earnedMediaValue?: number;
  estimatedImpressions?: number;
  estimatedReach?: number;
  exits?: number;
  followerEngagementRate?: number;
  impressions?: number;
  impressionsEngagementRate?: number;
  interactions?: number;
  likes?: number;
  notes?: number;
  reach?: number;
  reachEngagementRate?: number;
  repins?: number;
  replies?: number;
  retweets?: number;
  saves?: number;
  shares?: number;
  tapsBack?: number;
  tapsForward?: number;
  videoViews?: number;
}

export interface IContentItemInstagramLocation {
  id: string;
  name?: string;
  lat?: number;
  lon?: number;
}

export interface IContentItemSocialDetails {
  caption?: string;
  destinationUrl?: string;
  filter?: string;
  hashtags: string[];
  instagramPostType?: InstagramPostType;
  isInstagramStory?: boolean;
  link: string;
  location?: IContentItemInstagramLocation;
  mentionedUsers: IContentItemTaggedUser[];
  network: UniversalItemType;
  postedTimestamp?: number;
  rating?: number;
  socialNetwork?: SocialNetwork;
  stats?: IContentLibraryItemStats;
  taggedUsers: IContentItemTaggedUser[];
  user?: IContentItemSocialUser;
}

export interface IContentItemTaggedUser {
  id?: string;
  username: string;
}

export interface IContentItemSocialUser {
  fullName?: string;
  id?: string;
  isBusiness?: boolean;
  profilePicture?: string;
  stats?: IContentItemSocialUserStats;
  username: string;
}

export interface IContentItemSocialUserStats {
  followers?: number;
  following?: number;
  posts?: number;
}

export interface IContentAggregateMediaStats {
  avgComments?: number;
  avgCompletionRate?: number;
  avgImpressions?: number;
  avgLikes?: number;
  avgPotentialImpressions?: number;
  avgReach?: number;
  avgRepins?: number;
  earnedMediaCurrency?: string;
  engagementRate?: number;
  followerEngagementRate?: number;
  igPercentage?: number;
  pinPercentage?: number;
  potentialImpressions?: number;
  reachEngagementRate?: number;
  storyPercentage?: number;
  totalComments?: number;
  totalComputedAggregateImpressions?: number;
  totalEarnedMediaValue?: number;
  totalEngagement?: number;
  totalImpressions?: number;
  totalItems?: number;
  totalLikes?: number;
  totalReach?: number;
  totalRepins?: number;
}

export interface IContentLibraryItemsResponse {
  contentItems: IContentLibraryItem[];
  contentStats?: IContentAggregateMediaStats;
  numAvailable?: number;
  totalCount?: number;
}

export interface IDeserializedContentLibraryItemsResponse
  extends Omit<IContentLibraryItemsResponse, 'contentItems'> {
  contentItems: IDeserializedContentLibraryItem[];
}

export interface IContentLibraryItemsDataResponse {
  data: IContentLibraryItemsResponse;
}

export type AutocompleteType =
  | 'InstagramUsersAndHashtags'
  | 'Labels'
  | 'VisualTags';

export interface IContentLibraryAutocompleteRequest
  extends IContentLibraryItemParams {
  prefix: string;
  autocompleteType: AutocompleteType;
}

export interface IContentLibraryAutocomplete {
  value: string;
  count: number;
}

export interface IContentLibraryAutocompleteResponse {
  text?: string;
  hashtags: IContentLibraryAutocomplete[];
  usernames: IContentLibraryAutocomplete[];
  labels: IContentLibraryAutocomplete[];
}

export enum SocialFilterOperand {
  ALL = 'all',
  ANY = 'any',
}

export interface IAggregationSocialFiltersRequest {
  igHashtags: string[];
  igUsernames: string[];
  socialFilterOperand: SocialFilterOperand;
}

export interface IContentLibraryAggregationRequest {
  exclusions?: IAggregationSocialFiltersRequest;
  filters?: IContentLibraryFiltersOptions;
  sortByDistanceTo?: string;
  sources?: IContentLibrarySourcesOptions;
  termsLimit?: number;
  type: AutocompleteType;
}

export interface TermAggregation {
  count: number;
  tag: string;
}

export interface IContentLibraryAggregationResponse {
  visualTags: TermAggregation[];
}

// In sync with MLS DistributionEndpoint
export enum DISTRIBUTION_ENDPOINTS {
  like2Buy = 'Like2Buy',
  scheduler = 'Scheduler',
  fanreel = 'Fanreel',
  reveal = 'Reveal',
  revolve = 'Revolve',
  showroom = 'Showroom',
  connect = 'Connect',
  tiktokLike2Buy = 'TikTokLike2Buy',
}

export interface IContentItemMISDetails {
  user: {
    fullName: string;
    email?: string;
    emailMd5?: string;
  };
}

export interface IContentLibraryUploadRequest {
  collectionId?: CollectionId;
  file: File;
  init?: RequestInit;
  uploadProgress: (xhr: XMLHttpRequest, progress: number) => void;
}

export interface IContentImportFromUrlRequest {
  collectionIds?: CollectionId[];
  labels?: string[];
  metadata?: IContentImportFromUrlRequestMetadata;
  url: string;
}

export interface IContentImportFromUrlRequestMetadata {
  ingestionSource?: MediaImportItemIngestionSource;
  ingestionSourceItemId?: string;
  name?: string;
  sourceUrl?: string;
}

export interface IContentLibraryProcessingResponse {
  savedMediaIds: string[];
}

export interface IContentLibraryUploadResponse {
  id: string;
  videoId?: string;
  videoStatus?: 'Failed' | 'Processing' | 'Ready' | 'Unknown';
}

export interface ContentLibrarySettingsRequest {
  // Note: other properties exist that we are not currently making use of here
  showCollectionsFilterTooltip?: boolean;
}

export interface ContentLibrarySettingsResponse {
  // Note: other unused properties exist that we are currently ignoring for now e.g. defaultFilters
  showCollectionsFilterTooltip?: boolean;
  userIdsSelection: string[];
}

export interface ContentLibraryExportCollectionRequest {
  collectionId: string;
  filters?: IContentLibraryFiltersOptions;
  sources?: IContentLibrarySourcesOptions;
  itemIds?: PartialCuid[];
  hydrateWithDeetsItems?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: ContentLibrarySortType;
}

export enum ContentLibrarySortType {
  ADDED_TO_COLLECTION_TIMESTAMP = 'AddedToCollectionTimestamp',
  ADDED_TO_LIBRARY_TIMESTAMP = 'AddedToLibraryTimestamp',
  COMMENTS = 'comments',
  ENGAGEMENT = 'engagement',
  EMV = 'EMV',
  IMPRESSIONS = 'Impressions',
  TOTAL_ENGAGEMENT = 'totalEngagement',
  FANREEL_LAST_MODERATED_TIMESTAMP = 'FanreelLastModeratedTimestamp',
  FOLLOWERS = 'Followers',
  LIKES = 'likes',
  TIME = 'PostedTimestamp',
  VISUAL_SCORE = 'visualScore',
}

export enum ContentLibrarySourcesGroups {
  ALL_BAZAARVOICE = 'AllBazaarvoice',
  ALL_BRAND_POSTS = 'AllBrandPosts',
  ALL_DROPBOX = 'AllDropbox',
  ALL_INSTAGRAM = 'AllInstagram',
  ALL_MEDIA = 'AllMedia',
  ALL_PINTEREST = 'AllPinterest',
  ALL_TIKTOK = 'AllTikTok',
  ALL_TRACKED_COMPETITORS = 'AllTrackedCompetitors',
  ALL_TRACKED_HASHTAGS = 'AllTrackedHashtags',
  ALL_TRACKED_USERS = 'AllTrackedUsers',
  ALL_YOUTUBE = 'AllYouTube',
  TEAM_MEDIA = 'TeamMedia',
}

export enum ContentLibraryContentGroup {
  ADDED_TO_COLLECTION = 'AddedToCollection',
  ADDED_TO_LIBRARY = 'AddedToLibrary',
  // All is used by the "Legacy" library as the "All touched media" view
  ALL = 'All',
  // All media is used by Collections as "Everything associated with your client at all" view
  ALL_MEDIA = 'AllMedia',
  COLLECTION = 'Collection',
  // Represents an empty group
  NOTHING = 'Nothing',
  TEAM = 'Team',
}

export enum MediaImportItemIngestionSource {
  Bookmarklet = 'Bookmarklet',
  Dropbox = 'Dropbox',
  Email = 'Email',
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  Olapic = 'Olapic',
  Pinterest = 'Pinterest',
  Pixlee = 'Pixlee',
  SocialAnnex = 'SocialAnnex',
  Tumblr = 'Tumblr',
  Twitter = 'Twitter',
  Unknown = 'Unknown',
  UploadDesktop = 'UploadDesktop',
  UploadMobile = 'UploadMobile',
}

export type MediaImportItemIngestionSources =
  | 'Bookmarklet'
  | 'Dropbox'
  | 'Email'
  | 'Facebook'
  | 'Instagram'
  | 'Olapic'
  | 'Pinterest'
  | 'Pixlee'
  | 'SocialAnnex'
  | 'Tumblr'
  | 'Twitter'
  | 'Unknown'
  | 'UploadDesktop'
  | 'UploadMobile'
  | (string & {});

export interface CampaignContentRequestSource {
  campaignIds: string[];
  campaignSource: CampaignSource;
}

// This is set to avoid exceeding the API limit for content CSV exports
export const MAX_EXPORTABLE_ITEMS = 1000;
